/**
 * User Management Page
 *
 * Super-admin-only page for viewing users and assigning Edna roles.
 *
 * The role vocabulary is ordered and has four values — see `auth/roles.ts`. This
 * screen replaced a binary promote/demote pair, so the confirmation copy derives
 * direction (promotion vs demotion) from the ordering rather than from a boolean.
 */

import React, { useState } from 'react';
import { Card, Stack, Flex, Button, Select, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { useUsers, useUserMutations } from '../../api/users';
import { useCallerRole } from '../../auth/useCallerRole';
import { GATES, ROLE_ORDER, ROLE_LABELS, atLeast, type EdnaRole } from '../../auth/roles';
import { LoadingState } from '../shared/LoadingState';
import { ErrorDisplay } from '../shared/ErrorDisplay';
import { ConfirmModal } from '../shared/ConfirmModal';
import type { InternalUser } from '../../api/types';
import { errorMessage } from '../../api/errors';

/** Roles a super-admin may assign, most-privileged first for the selector. */
const ASSIGNABLE_ROLES = [...ROLE_ORDER].reverse();

/**
 * Read the selected value out of the design system `Select`'s onChange argument.
 *
 * ⚠️ The DS `Select` has two incompatible `onChange` signatures depending on
 * version, and this repo is exposed to both:
 *
 * - **v0.1.1** (what `package-lock.json` pins, and what CI's `npm ci` installs)
 *   wraps a native `<select>`, so onChange receives a React ChangeEvent.
 * - **The DS `main` branch** has a rewritten custom dropdown whose onChange
 *   receives the value **string** directly.
 *
 * `package.json` requests `#main` while the lockfile pins an older commit, so a
 * lockfile refresh would flip the signature. Handling both is a few lines and
 * turns a silent, hard-to-spot break in role assignment into a non-event.
 */
function selectedValue(arg: unknown): string | null {
  if (typeof arg === 'string') return arg;
  const value = (arg as { target?: { value?: unknown } } | null | undefined)?.target?.value;
  return typeof value === 'string' ? value : null;
}

export function UserManagementPage() {
  const { theme } = useTheme();
  const { data: users, loading, error, refetch } = useUsers();
  const callerRole = useCallerRole();
  const { updateRole } = useUserMutations();

  const [roleChangeTarget, setRoleChangeTarget] = useState<{
    user: InternalUser;
    newRole: EdnaRole;
  } | null>(null);
  const [updating, setUpdating] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  const handleRoleChange = async () => {
    if (!roleChangeTarget) return;
    setUpdating(true);
    setErrorMsg('');
    try {
      await updateRole(roleChangeTarget.user.id, roleChangeTarget.newRole);
      setRoleChangeTarget(null);
      refetch();
    } catch (err: unknown) {
      // Surface the service's own explanation rather than guessing at it. The API
      // returns a specific `detail` for each refusal — most importantly for the
      // last-super_admin guard, where a silent failure would look like success.
      setErrorMsg(errorMessage(err, 'Failed to update role.'));
    } finally {
      setUpdating(false);
    }
  };

  // Whole-screen gate. Loading state first, so a super-admin does not see
  // access-denied flash on every page load.
  if (callerRole.isLoading) {
    return <LoadingState message="Checking permissions..." />;
  }

  if (!callerRole.atLeast(GATES.userAdmin)) {
    return (
      <Stack gap="lg">
        <h1 style={{ ...textStyles.h3, color: textColor }}>User Management</h1>
        <Card variant="outlined">
          <Card.Body>
            <Flex direction="column" align="center" style={{ padding: spacing.lg }}>
              <span style={{ color: theme.colors.semantic.error.default, fontSize: fontSize.sm }}>
                Access denied. {ROLE_LABELS[GATES.userAdmin]} role required.
              </span>
            </Flex>
          </Card.Body>
        </Card>
      </Stack>
    );
  }

  if (loading) return <LoadingState message="Loading users..." />;
  if (error) return <ErrorDisplay error={error} onRetry={refetch} />;

  const isPromotion =
    roleChangeTarget !== null &&
    atLeast(roleChangeTarget.newRole, roleChangeTarget.user.role) &&
    roleChangeTarget.newRole !== roleChangeTarget.user.role;

  /** Badge tint scales with privilege so the four roles are distinguishable. */
  const roleBadgeStyle = (role: EdnaRole): React.CSSProperties => {
    const emphasis: Record<EdnaRole, { bg: string; fg: string }> = {
      standard: { bg: `${theme.colors.neutral[300]}20`, fg: mutedColor },
      data_science: { bg: `${theme.colors.primary[600]}10`, fg: theme.colors.primary[600] },
      engineer: { bg: `${theme.colors.primary[600]}20`, fg: theme.colors.primary[600] },
      super_admin: {
        bg: `${theme.colors.semantic.warning.default}20`,
        fg: theme.colors.semantic.warning.default,
      },
    };
    const { bg, fg } = emphasis[role];
    return {
      fontSize: fontSize.xs,
      padding: '2px 8px',
      borderRadius: '4px',
      fontWeight: 600,
      width: 'fit-content',
      whiteSpace: 'nowrap',
      background: bg,
      color: fg,
    };
  };

  return (
    <Stack gap="lg">
      <div>
        <h1 style={{ ...textStyles.h3, color: textColor }}>User Management</h1>
        <p style={{ margin: 0, marginTop: spacing.xs, fontSize: fontSize.sm, color: mutedColor }}>
          View and assign Edna roles. Roles are ordered — each grants everything below it.
        </p>
      </div>

      {errorMsg && (
        <span style={{ color: theme.colors.semantic.error.default, fontSize: fontSize.sm }}>{errorMsg}</span>
      )}

      <Card variant="elevated">
        <Card.Body>
          {users && users.length > 0 ? (
            <div style={{ border: `1px solid ${borderColor}`, borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 140px 180px',
                padding: `${spacing.sm} ${spacing.md}`,
                background: theme.colors.background.paper,
                borderBottom: `1px solid ${borderColor}`,
                fontSize: fontSize.xs,
                fontWeight: 600,
                color: mutedColor,
                textTransform: 'uppercase',
              }}>
                <span>Name</span>
                <span>Email</span>
                <span>Role</span>
                <span style={{ textAlign: 'right' }}>Assign Role</span>
              </div>
              {users.map((user) => (
                <div key={user.id} style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 140px 180px',
                  padding: `${spacing.sm} ${spacing.md}`,
                  borderBottom: `1px solid ${borderColor}`,
                  fontSize: fontSize.sm,
                  alignItems: 'center',
                }}>
                  <span style={{ fontWeight: 500, color: textColor }}>{user.name}</span>
                  {/*
                    Email is nullable: Cognito access tokens carry no email claim, so
                    users provisioned after the api-edna cutover have none stored until
                    the service looks it up (fti-sc/api_edna#5).
                  */}
                  <span style={{ color: mutedColor }}>{user.email ?? '—'}</span>
                  <span style={roleBadgeStyle(user.role)}>{ROLE_LABELS[user.role]}</span>
                  <Flex justify="end">
                    <Select
                      value={user.role}
                      disabled={updating}
                      onChange={(arg: unknown) => {
                        const next = selectedValue(arg);
                        if (next && next !== user.role) {
                          setRoleChangeTarget({ user, newRole: next as EdnaRole });
                        }
                      }}
                      options={ASSIGNABLE_ROLES.map((role) => ({
                        value: role,
                        label: ROLE_LABELS[role],
                      }))}
                    />
                  </Flex>
                </div>
              ))}
            </div>
          ) : (
            <Flex direction="column" align="center" style={{ padding: spacing.lg }}>
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>No users found.</span>
            </Flex>
          )}
        </Card.Body>
      </Card>

      <ConfirmModal
        isOpen={!!roleChangeTarget}
        title={isPromotion ? 'Promote User' : 'Change User Role'}
        message={
          roleChangeTarget
            ? `Change "${roleChangeTarget.user.name}" from ${ROLE_LABELS[roleChangeTarget.user.role]} ` +
              `to ${ROLE_LABELS[roleChangeTarget.newRole]}? ` +
              (isPromotion
                ? 'They will gain the additional permissions of that role.'
                : 'They will lose the permissions above that role.')
            : ''
        }
        confirmLabel={isPromotion ? 'Promote' : 'Change Role'}
        variant={isPromotion ? 'default' : 'warning'}
        onConfirm={handleRoleChange}
        onCancel={() => setRoleChangeTarget(null)}
      />
    </Stack>
  );
}
